/**
 * チャット添付用の画像ファイルを検査し、必要に応じて縮小・再エンコードして
 * base64 データへ変換するユーティリティ。
 */

import { fetchFile } from "@/lib/download-file";

// バックエンドの ChatMessageContentValidator と制限値を同期すること。
// Izanagi 共通の画像形式制限 (現行値は Anthropic 推奨値由来)。
export const ACCEPTED_IMAGE_MEDIA_TYPES = [
	"image/png",
	"image/jpeg",
	"image/gif",
	"image/webp",
] as const;

// バックエンドの ChatMessageContentValidator と制限値を同期すること。
// 1 メッセージに添付できる画像の最大枚数。
export const MAX_IMAGES_PER_MESSAGE = 4;

// バックエンドの ChatMessageContentValidator と制限値を同期すること。
// 画像 1 枚あたりの最大バイト数。
export const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

// Izanagi 共通の最大長辺 (px、Anthropic の多枚数リクエスト時の上限由来。バックエンドの ManyImageLimit と同期すること)。これを超える場合は縮小する。
const MAX_LONG_EDGE = 2000;

// このバイト数以下かつ長辺が MAX_LONG_EDGE 以下であれば、再エンコードせずそのまま送る。
const PASSTHROUGH_MAX_BYTES = 1.5 * 1024 * 1024;

const IMAGE_READ_ERROR = "画像を読み込めませんでした。";
const UNSUPPORTED_IMAGE_ERROR =
	"対応していない画像形式です。PNG / JPEG / GIF / WebP を使用してください。";

/** チャットコンポーザーに保持する、送信可能な状態まで加工済みの画像。 */
export interface ComposerImage {
	id: string;
	/** 最終的にエンコードされた画像の media_type。 */
	mediaType: string;
	/** base64 エンコードされた画像データ (`data:` プレフィックスなし)。 */
	data: string;
	width: number;
	height: number;
	byteSize: number;
	name: string;
}

/** `planImageTransform` が返す、画像添付前に必要な変換方針。 */
export type ImageTransformPlan =
	| {
			action: "reject";
			reason: "unsupported" | "too_large" | "too_large_dimensions" | "invalid";
	  }
	| { action: "passthrough" }
	| { action: "reencode"; targetWidth: number; targetHeight: number };

function isAcceptedImageType(type: string): type is (typeof ACCEPTED_IMAGE_MEDIA_TYPES)[number] {
	return (ACCEPTED_IMAGE_MEDIA_TYPES as readonly string[]).includes(type);
}

/**
 * 画像の種別・サイズ・寸法から、添付前にどのような変換が必要かを判定する。
 * DOM に依存しない純粋な判定のみを行い、実際のデコードや再エンコードは呼び出し側が行う。
 */
export function planImageTransform(input: {
	type: string;
	byteSize: number;
	width: number;
	height: number;
}): ImageTransformPlan {
	if (!isAcceptedImageType(input.type)) {
		return { action: "reject", reason: "unsupported" };
	}

	if (input.width <= 0 || input.height <= 0) {
		return { action: "reject", reason: "invalid" };
	}

	const longEdge = Math.max(input.width, input.height);

	// GIF は canvas で再エンコードすると1フレームの静止画になりアニメーションを失うため、縮小せず無変換で扱う。
	if (input.type === "image/gif") {
		if (input.byteSize > MAX_IMAGE_BYTES) return { action: "reject", reason: "too_large" };
		if (longEdge > MAX_LONG_EDGE) return { action: "reject", reason: "too_large_dimensions" };
		return { action: "passthrough" };
	}

	if (longEdge <= MAX_LONG_EDGE && input.byteSize <= PASSTHROUGH_MAX_BYTES) {
		return { action: "passthrough" };
	}

	const scale = Math.min(1, MAX_LONG_EDGE / longEdge);
	return {
		action: "reencode",
		targetWidth: Math.round(input.width * scale),
		targetHeight: Math.round(input.height * scale),
	};
}

/** 画像添付の処理中に発生した、ユーザーにそのまま表示できるエラー。 */
export class ImageAttachmentError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "ImageAttachmentError";
	}
}

function readAsDataUrl(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => {
			console.error("FileReader failed to read blob", reader.error);
			reject(new ImageAttachmentError(IMAGE_READ_ERROR));
		};
		reader.readAsDataURL(blob);
	});
}

async function toBase64(blob: Blob): Promise<string> {
	const dataUrl = await readAsDataUrl(blob);
	const base64 = dataUrl.split(",")[1];
	if (!base64) throw new ImageAttachmentError(IMAGE_READ_ERROR);
	return base64;
}

async function toComposerImage(
	blob: Blob,
	mediaType: string,
	width: number,
	height: number,
	name: string,
): Promise<ComposerImage> {
	return {
		id: crypto.randomUUID(),
		mediaType,
		data: await toBase64(blob),
		width,
		height,
		byteSize: blob.size,
		name,
	};
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) resolve(blob);
				else reject(new ImageAttachmentError(IMAGE_READ_ERROR));
			},
			type,
			quality,
		);
	});
}

function drawToCanvas(
	bitmap: ImageBitmap,
	width: number,
	height: number,
	fillWhiteBackground: boolean,
): HTMLCanvasElement {
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) throw new ImageAttachmentError(IMAGE_READ_ERROR);
	ctx.imageSmoothingQuality = "high";
	if (fillWhiteBackground) {
		// ds-allow-raw-color: アップロードする成果物の canvas 合成であり UI ではない。
		// モデルは白背景の PNG を前提とする。
		ctx.fillStyle = "#fff";
		ctx.fillRect(0, 0, width, height);
	}
	ctx.drawImage(bitmap, 0, 0, width, height);
	return canvas;
}

async function reencodeImage(
	bitmap: ImageBitmap,
	sourceType: string,
	targetWidth: number,
	targetHeight: number,
	name: string,
): Promise<ComposerImage> {
	const encode = (type: string, quality: number, fillWhiteBackground: boolean) =>
		canvasToBlob(
			drawToCanvas(bitmap, targetWidth, targetHeight, fillWhiteBackground),
			type,
			quality,
		);

	const encodedType = sourceType === "image/jpeg" ? "image/jpeg" : "image/webp";
	let blob = await encode(encodedType, 0.85, false);

	if (blob.size > MAX_IMAGE_BYTES) {
		// 1回目が上限を超える場合 (WebP 非対応の Safari が PNG にフォールバックした場合を含む)、
		// 透過を白背景に潰した上で JPEG として再エンコードする。
		blob = await encode("image/jpeg", 0.8, true);
		if (blob.size > MAX_IMAGE_BYTES) {
			throw new ImageAttachmentError("画像が大きすぎます。縮小後も4MBを超えています。");
		}
	}

	// toBlob へ指定した type がブラウザで非対応の場合、フォールバック先の実際の type を採用する。
	return toComposerImage(blob, blob.type, targetWidth, targetHeight, name);
}

/**
 * 画像ファイルを検査し、チャット添付に使える形 (base64 + メタデータ) へ変換する。
 * 対応形式外・破損ファイル・縮小後もサイズ超過の場合は `ImageAttachmentError` を投げる。
 */
export async function processImageFile(file: File): Promise<ComposerImage> {
	if (!isAcceptedImageType(file.type)) {
		throw new ImageAttachmentError(UNSUPPORTED_IMAGE_ERROR);
	}

	// resize オプションは Safari が非対応のため使わない。取得した bitmap は寸法参照と描画の両方に使う。
	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(file);
	} catch (err) {
		console.error("createImageBitmap failed", err);
		throw new ImageAttachmentError(IMAGE_READ_ERROR);
	}

	try {
		const plan = planImageTransform({
			type: file.type,
			byteSize: file.size,
			width: bitmap.width,
			height: bitmap.height,
		});
		const name = file.name.length > 0 ? file.name : "貼り付け画像";

		switch (plan.action) {
			case "reject":
				throw new ImageAttachmentError(
					plan.reason === "too_large"
						? "画像が大きすぎます。"
						: plan.reason === "too_large_dimensions"
							? `GIF 画像の縦横は ${MAX_LONG_EDGE}px 以下にしてください。`
							: plan.reason === "invalid"
								? IMAGE_READ_ERROR
								: UNSUPPORTED_IMAGE_ERROR,
				);
			case "passthrough":
				return await toComposerImage(file, file.type, bitmap.width, bitmap.height, name);
			case "reencode":
				return await reencodeImage(bitmap, file.type, plan.targetWidth, plan.targetHeight, name);
		}
	} finally {
		bitmap.close();
	}
}

/** 履歴 API が本体を外した画像を画像エンドポイントから取り直す。 */
export async function fetchImageBlob(src: string): Promise<Blob> {
	const res = await fetchFile(src, { credentials: "include" });
	if (!res.ok) throw new ImageAttachmentError(`${IMAGE_READ_ERROR} (${res.status})`);
	return await res.blob();
}
